'use client';
import React, { useMemo, useState, useEffect } from 'react';

import styles from './ProjectTracker.module.css';
import { toast } from 'react-toastify';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Typography from '@mui/material/Typography';
import { Tooltip, IconButton } from '@mui/material';
import PlagiarismIcon from '@mui/icons-material/Plagiarism';

import { AgChartsCommunityModule } from 'ag-charts-community';
import {
  ModuleRegistry,
  ClientSideRowModelModule,
  ColumnApiModule,
  CellStyleModule,
  TooltipModule,
  NumberEditorModule,
  NumberFilterModule,
  TextEditorModule,
  TextFilterModule,
  DateEditorModule,
  DateFilterModule,
  ValidationModule,
} from 'ag-grid-community';

import {
  RichSelectModule,
  IntegratedChartsModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  SideBarModule,
} from 'ag-grid-enterprise';

import { AgGridReact } from 'ag-grid-react';

import useFetchProjectTracker from '../../hooks/useFetchProjectTracker';
import useFetchActivityData from '../../hooks/useFetchActivityData';
import useFetchProjectPurchaseOrders from '../../hooks/useFetchProjectPurchaseOrders';
import useSaveProjectTableEdits from '../../hooks/useSaveProjectTableEdits';

import ProgressBarCellRenderer from '../../components/ProjectTracker/ProgressBarCellRenderer';
import ProjectNotes from '../../components/ProjectTracker/ProjectNotes';
import AdaptiveCost from '../../components/ProjectTracker/AdaptiveCost';
import HeatMapCommits from '../../components/ProjectTracker/HeatmapCommits';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ColumnApiModule,
  CellStyleModule,
  TooltipModule,
  NumberEditorModule,
  NumberFilterModule,
  TextEditorModule,
  TextFilterModule,
  DateEditorModule,
  DateFilterModule,
  ValidationModule,
  RichSelectModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  SideBarModule,
  IntegratedChartsModule.with(AgChartsCommunityModule),
]);

const ProjectManagerCustomHeader = (props) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
    <PersonIcon style={{ fontSize: '24px', color: 'white' }} />
    <span>{props.displayName}</span>
  </div>
);

const ProjectTracker = () => {
  const [costLoading, setCostLoading] = useState(false);
  const [alignment, setAlignment] = useState('all');
  const [gridApi, setGridApi] = useState(null);

  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedView, setSelectedView] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [changedRows, setChangedRows] = useState([]);
  const [hasRestoredColumnState, setHasRestoredColumnState] = useState(false);

  const { projects, loading, error, refetch } = useFetchProjectTracker();
  const { fetchActivityData } = useFetchActivityData();
  const { fetchProjectPurchaseOrders } = useFetchProjectPurchaseOrders();
  const { SaveProjectTableEdits } = useSaveProjectTableEdits();

  const isMobile = useMediaQuery('(max-width:768px)');

  const getColumnDefs = (alignment) => {
    switch (alignment) {
      case 'netsuite':
        return [
          {
            field: 'description',
            headerName: 'AFE Description',
            headerClass: styles.netsuiteHeader,
            headerTooltip: 'NetSuite Capex AFE project description',
          },
          {
            field: 'netSuiteId',
            headerName: 'NetSuite ID',
            headerClass: styles.netsuiteHeader,
            headerTooltip: 'NetSuite Internal ID',
          },
          {
            field: 'entity',
            headerName: 'Asset',
            headerClass: styles.netsuiteHeader,
            headerTooltip: 'NetSuite Asset Name',
            filter: true,
          },
          {
            field: 'afeNumber',
            headerName: 'AFE Number',
            headerClass: styles.netsuiteHeader,
            headerTooltip: 'NetSuite AFE Number',
            filter: true,
          },
          {
            field: 'projectManager',
            headerName: 'Proj. Manager',
            headerClass: styles.netsuiteHeader,
            headerTooltip: 'Project Manager Name',
            filter: true,
          },
          {
            field: 'pepApprovalDate',
            headerName: 'PEP Approval Date',
            headerClass: styles.netsuiteHeader,
            headerTooltip: 'Project Start Date / AFE Kickoff Date',
          },
        ];
      case 'projectmanager':
        return [
          {
            field: 'portfolioProject',
            headerName: 'Portfolio Project',
            minWidth: 220,
            editable: true,
            headerComponent: ProjectManagerCustomHeader,
            headerClass: styles.pmHeader,
            headerTooltip:
              'Projects grouped within a given Entity based on falling within the same finaincing package or by someother project relationship.  E.g., The Project Portfolio "Matterhorn Expansion" encompasses compression, metering and pipeline projects, all of which fall under the Matterhorn Entity',
          },
          {
            field: 'type',
            headerName: 'Type',
            editable: true,
            filter: true,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorParams: {
              values: ['Compression', 'Lateral', 'Meter', 'Other', 'Pipeline', 'Pump Station'],
            },
            headerComponent: ProjectManagerCustomHeader,
            headerClass: styles.pmHeader,
            headerTooltip:
              'One of the project types as follows Compression, Metering, Pipeline, Pump Station, Lateral or Other',
          },
          {
            field: 'namePlateCapacity',
            headerName: 'Name Plate Cap.',
            minWidth: 220,
            editable: true,
            headerComponent: ProjectManagerCustomHeader,
            headerClass: styles.pmHeader,
            headerTooltip:
              'The rated design capacity of the project. Note: this may differ from the capacity stated in the ICA',
          },
          {
            field: 'unit',
            headerName: 'Unit',
            editable: true,
            headerComponent: ProjectManagerCustomHeader,
            headerClass: styles.pmHeader,
            headerTooltip: 'Units of volume, mass or energy per time associated with the Name Plate Capacity',
          },
          {
            field: 'overallComplete',
            headerName: 'Overall Complete %',
            minWidth: 240,
            editable: true,
            cellEditor: 'agNumberCellEditor',
            headerComponent: ProjectManagerCustomHeader,
            headerClass: styles.pmHeader,
            headerTooltip: 'Overall percentage of project completion',
            valueGetter: (params) => params.data?.overallComplete ?? 0,
          },
          {
            field: 'estReadyForService',
            headerName: 'Est. Ready Date',
            editable: true,
            cellEditor: 'agDateStringCellEditor',
            headerComponent: ProjectManagerCustomHeader,
            headerClass: styles.pmHeader,
            headerTooltip: 'Estimated AFE In-Service Date',
          },
          {
            field: 'icaApprovalDate',
            headerName: 'ICA Approval Date',
            editable: true,
            cellEditor: 'agDateStringCellEditor',
            headerComponent: ProjectManagerCustomHeader,
            headerClass: styles.pmHeader,
            headerTooltip:
              'The date on which both parties have executed an Interconnect Agreement associated with the project',
          },
          {
            field: 'requiredReadyForService',
            headerName: 'Required Ready Date',
            editable: true,
            cellEditor: 'agDateStringCellEditor',
            headerComponent: ProjectManagerCustomHeader,
            headerClass: styles.pmHeader,
            headerTooltip:
              'Any contractually required date at which the project must be ready to operate as per the Interconnect Agreement, Transportation Service Agreement or other contractual documents',
          },
          {
            field: 'baselineReadyForService',
            headerName: 'Baseline Ready Date',
            editable: true,
            cellEditor: 'agDateStringCellEditor',
            headerComponent: ProjectManagerCustomHeader,
            headerClass: styles.pmHeader,
            headerTooltip:
              'The date, as set within the project execution plan or scope of work, at which the project will be Ready for Service',
          },
          {
            field: 'mechanicalComplete',
            headerName: 'Mechanical Complete Date',
            editable: true,
            cellEditor: 'agDateStringCellEditor',
            headerComponent: ProjectManagerCustomHeader,
            headerClass: styles.pmHeader,
            headerTooltip:
              'The date the project is deemed mechanically complete; Mechanically Complete being the date at which all safety critical startup items are complete',
          },
          {
            field: 'dateAfeCloseRequestSent',
            headerName: 'Close Request Sent Date',
            editable: true,
            cellEditor: 'agDateStringCellEditor',
            headerComponent: ProjectManagerCustomHeader,
            headerClass: styles.pmHeader,
            headerTooltip:
              'The date on which notification was made within NetSuite to close the AFE associated with the project to billing (also known as the notice of 60-day close)',
          },
          {
            field: 'rowAcquired',
            headerName: 'ROW Acquired',
            editable: true,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorParams: {
              values: ['Complete', 'Not Complete', 'NA'],
            },
            headerComponent: ProjectManagerCustomHeader,
            headerClass: styles.pmHeader,
            headerTooltip: 'Complete if 100% of ROW is acquired. Not complete if less than 100% of ROW is acquired',
          },
          {
            field: 'enviroSurvey',
            headerName: 'Enviro. Survey',
            editable: true,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorParams: {
              values: ['Complete', 'Not Complete', 'NA'],
            },
            headerComponent: ProjectManagerCustomHeader,
            headerClass: styles.pmHeader,
            headerTooltip:
              'Complete if 100% of Environmental Surveys are finished. Not complete if less than 100% of Environmental Surveys are finished',
          },
          {
            field: 'detailedSurvey',
            headerName: 'Detailed Survey',
            editable: true,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorParams: {
              values: ['Complete', 'Not Complete', 'NA'],
            },
            headerComponent: ProjectManagerCustomHeader,
            headerClass: styles.pmHeader,
            headerTooltip:
              'Complete if 100% of Detailed Surveys are finished. Not complete if less than 100% of Detailed Surveys are finished',
          },
          {
            field: 'constructionMobd',
            headerName: 'Construction Mob',
            editable: true,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorParams: {
              values: ['Complete', 'Not Complete', 'NA'],
            },
            headerComponent: ProjectManagerCustomHeader,
            headerClass: styles.pmHeader,
            headerTooltip:
              'Complete if constractor mobilization has occurred. Not complete if contractor mobilization has not occurred',
          },
          {
            field: 'icaExecution',
            headerName: 'ICA Execution',
            editable: true,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorParams: {
              values: ['Complete', 'Not Complete', 'NA'],
            },
            headerComponent: ProjectManagerCustomHeader,
            headerClass: styles.pmHeader,
            headerTooltip:
              'Complete if the ICA is executed by both parties. Not complete if the ICA is pending execution by either party',
          },
          {
            field: 'readyForService',
            headerName: 'Ready for Service',
            editable: true,
            cellEditor: 'agRichSelectCellEditor',
            cellEditorParams: {
              values: ['Complete', 'Not Complete', 'NA'],
            },
            headerComponent: ProjectManagerCustomHeader,
            headerClass: styles.pmHeader,
            headerTooltip:
              'Complete if the project is Ready for Service. Not complete if the project is not Ready for Service',
          },
        ];
      case 'gis':
        return [
          {
            field: 'gisLifeCycleStatus',
            headerName: 'GIS Life Cycle Status',
            headerClass: styles.gisHeader,
            headerTooltip: 'This is the GIS Life Cycle Status Code.',
          },
          {
            field: 'gisInServiceDate',
            headerName: 'GIS In-Service Date',
            headerTooltip: 'GIS date when project is marked as In-Service',
            headerClass: styles.gisHeader,
            valueGetter: function (params) {
              const phase = params.data.gisLifeCycleStatus;
              if (phase === 'In Service') {
                return params.data.gisInServiceDate;
              } else {
                return null;
              }
            },
          },
        ];
      case 'quorum':
        return [
          {
            field: 'firstFlowDate',
            headerName: 'First Flow Date',
            headerClass: styles.quorumHeader,
            headerTooltip:
              'Date in which measurement is first captured (a date generally agreed upon between field and external field personal) and Shippers are able to nominate gas to flow to/from',
          },
          {
            field: 'qptmName',
            headerName: 'QPTM Name',
            headerClass: styles.quorumHeader,
            headerTooltip:
              'This name is on most contractual agreements (ICAs, OBAs, SOs), External Maps, and the reference name for shippers when nominating on our EBB. In some cases, the location name is discussed between Gas Controls',
          },
          {
            field: 'locId',
            headerName: 'Loc ID',
            headerClass: styles.quorumHeader,
            headerTooltip:
              'This ID is on most contractual agreements (ICAs, OBAs, SOs), External Maps, and the reference point ID for shippers when nominating on our EBB',
          },
        ];
      case 'workdayadaptive':
        return [
          {
            field: 'actuals',
            headerName: 'Actual Cost',
            headerTooltip: 'Sum of actuals to date.',
            filter: 'agNumberColumnFilter',
            headerClass: styles.adaptiveHeader,
            valueFormatter: (params) =>
              new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
          },

          {
            field: 'committed',
            headerName: 'Committed',
            headerTooltip: 'Sum of current remaining committed amounts.',
            filter: 'agNumberColumnFilter',
            headerClass: styles.adaptiveHeader,
            valueFormatter: (params) =>
              new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
          },

          {
            field: 'fixedCost',
            headerName: 'Fixed Cost',
            headerTooltip: 'Actuals + Committed',
            filter: 'agNumberColumnFilter',
            headerClass: styles.adaptiveHeader,
            valueFormatter: (params) =>
              new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
          },

          {
            headerName: 'Base Cost',
            field: 'baseCost',
            headerTooltip: 'Budget amount less unallocated contingency',
            filter: 'agNumberColumnFilter',
            headerClass: styles.adaptiveHeader,
            valueFormatter: (params) =>
              new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
          },

          {
            headerName: 'Budget',
            field: 'budgetCost',
            headerTooltip: 'Full budgeted amount.',
            filter: 'agNumberColumnFilter',
            headerClass: styles.adaptiveHeader,
            valueFormatter: (params) =>
              new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
          },

          {
            headerName: 'EFC',
            field: 'efc',
            filter: 'agNumberColumnFilter',
            headerClass: styles.adaptiveHeader,
            headerTooltip: 'Estimated final cost of the projects.',
            valueFormatter: (params) =>
              new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
          },
          {
            headerName: 'EFC to Budget',
            field: 'efcBudget',
            filter: 'agNumberColumnFilter',
            cellRenderer: 'progressBarCellRenderer',
            valueGetter: (params) => {
              const efc = params.data.efc;
              const budget = params.data.budgetCost;
              if (efc != null && budget != null && budget !== 0) {
                return (efc / budget) * 100;
              }
              return null;
            },
          },
          {
            headerName: 'Remaining Contingency',
            field: 'remainingContingency',
            headerTooltip: 'Amount of unallocated contingency remaining',
            filter: 'agNumberColumnFilter',
            headerClass: styles.adaptiveHeader,
            valueFormatter: (params) =>
              new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
          },

          {
            headerName: 'Over / Under Budget',
            field: 'overUnderBudget',
            headerTooltip: 'EFC - Budget',
            filter: 'agNumberColumnFilter',
            headerClass: styles.adaptiveHeader,
            valueFormatter: (params) =>
              new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
            cellStyle: (params) => {
              if (params.value < 0) {
                return { backgroundColor: '#00BBA1', color: 'white' };
              } else if ((params.value = 0 || params.value == null)) {
                return null;
              } else {
                return { backgroundColor: '#FF594B', color: 'white' };
              }
            },
          },

          {
            headerName: 'Contingency %',
            field: 'contingencyPct',
            headerTooltip: 'PCT of contingency remaining.',
            filter: 'agNumberColumnFilter',
            headerClass: styles.adaptiveHeader,
            cellRenderer: 'progressBarCellRenderer',
            valueGetter: function (params) {
              const contingencyPct = params.data.contingencyPct;
              if (contingencyPct != null) {
                return contingencyPct * 100;
              } else {
                return null;
              }
            },
          },

          {
            headerName: 'Total Cost Per Foot',
            field: 'totalCostPerFoot',
            headerTooltip: 'Budget/pipe length in feet',
            filter: 'agNumberColumnFilter',
            headerClass: styles.adaptiveHeader,
            valueFormatter: (params) =>
              new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
          },

          {
            headerName: '$ / In-Mile',
            field: 'costPerInMile',
            headerTooltip: 'EFC/Diameter/Miles',
            filter: 'agNumberColumnFilter',
            headerClass: styles.adaptiveHeader,
            valueFormatter: (params) =>
              new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
          },

          {
            headerName: '$ / ROD',
            field: 'costPerRod',
            headerTooltip: 'EFC/Pipe length in rods',
            filter: 'agNumberColumnFilter',
            headerClass: styles.adaptiveHeader,
            valueFormatter: (params) =>
              new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
          },

          {
            headerName: '$ / Foot ROW',
            field: 'costPerFootRow',
            headerTooltip: 'EFC for ROW/Pipe length in feet',
            filter: 'agNumberColumnFilter',
            headerClass: styles.adaptiveHeader,
            valueFormatter: (params) =>
              new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
          },

          {
            headerName: 'Cost Per BHP',
            field: 'costPerBhp',
            headerTooltip: 'EFC/BHP',
            filter: 'agNumberColumnFilter',
            headerClass: styles.adaptiveHeader,
            valueFormatter: (params) =>
              new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
          },

          {
            headerName: 'Variance %',
            field: 'variancePct',
            filter: 'agNumberColumnFilter',
            headerClass: styles.adaptiveHeader,
            cellRenderer: 'progressBarCellRenderer',
            valueGetter: function (params) {
              const variancePct = params.data.variancePct;
              if (variancePct != null) {
                return variancePct * 100;
              } else {
                return null;
              }
            },
          },

          {
            headerName: 'Remaining Forecast',
            field: 'remainingForecast',
            filter: 'agNumberColumnFilter',
            headerClass: styles.adaptiveHeader,
            valueFormatter: (params) =>
              new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
          },

          {
            headerName: 'Contingency Used',
            field: 'contingencyUsed',
            filter: 'agNumberColumnFilter',
            headerClass: styles.adaptiveHeader,
            valueFormatter: (params) =>
              new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(params.value),
          },
        ];
      case 'endcapnoEFC':
        return [
          {
            headerName: 'EFC to Budget',
            field: 'efcBudget',
            filter: 'agNumberColumnFilter',
            headerClass: styles.staticHeader,
            cellRenderer: 'progressBarCellRenderer',
            valueGetter: (params) => {
              const efc = params.data.efc;
              const budget = params.data.budgetCost;
              if (efc != null && budget != null && budget !== 0) {
                return (efc / budget) * 100;
              }
              return null;
            },
          },
          { field: 'lastModifiedDate', headerClass: styles.staticHeader, headerName: 'Last Modified' },
        ];
      case 'endcapwithEFC':
        return [{ field: 'lastModifiedDate', headerClass: styles.staticHeader, headerName: 'Last Modified' }];
    }
  };

  const staticColDefs = [
    {
      field: 'actions',
      headerName: 'View',
      width: 140,
      minWidth: 140,
      pinned: 'left',
      cellStyle: { textAlign: 'center' },
      cellRenderer: (params) => (
        <Tooltip title="View Details" arrow>
          <IconButton color="primary" size="small" onClick={() => handleViewCostClick(params)} disabled={costLoading}>
            {costLoading ? <CircularProgress size={20} /> : <PlagiarismIcon />}
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: 'ProjectPhase',
      headerName: 'Project Phase',
      headerClass: styles.staticHeader,
      pinned: 'left',
      headerTooltip: 'NetSuite project phase status',
    },
    {
      field: 'AFEName',
      headerName: 'AFE Name',
      headerClass: styles.staticHeader,
      pinned: 'left',
      headerTooltip: 'NetSuite Capex AFE Number/Name',
      width: 300,
    },
  ];

  const getAllDynamicColumns = () => [
    ...getColumnDefs('netsuite'),
    ...getColumnDefs('projectmanager'),
    ...getColumnDefs('gis'),
    ...getColumnDefs('quorum'),
    ...getColumnDefs('workdayadaptive'),
  ];

  const fullColumnDefs = [...staticColDefs, ...getAllDynamicColumns(), ...getColumnDefs('endcapwithEFC')];

  function setColumnVisibility(api, alignment) {
    if (!api) return;

    const allFields = getAllDynamicColumns().map((col) => col.field);
    const columnDefs = getColumnDefs(alignment) || [];
    const visibleFields = alignment === 'all' ? allFields : columnDefs.map((col) => col.field).filter(Boolean);

    const fieldsToHide = allFields.filter((field) => !visibleFields.includes(field));

    if (Array.isArray(visibleFields)) api.setColumnsVisible(visibleFields, true);
    if (Array.isArray(fieldsToHide)) api.setColumnsVisible(fieldsToHide, false);
  }

  useEffect(() => {
    if (gridApi && fullColumnDefs?.length && !hasRestoredColumnState) {
      // Give the grid time to initialize full column structure
      setTimeout(() => {
        applyTableState(gridApi);
        setHasRestoredColumnState(true);
      }, 50); // slightly delayed to let groups form
    }
  }, [gridApi, fullColumnDefs, hasRestoredColumnState]);

  useEffect(() => {
    refetch();
  }, [selectedView, selectedRow, refetch]);

  useEffect(() => {
    if (selectedRow?.id) {
      const updated = projects.find((proj) => proj.id === selectedRow.id);
      if (updated) {
        setSelectedRow(updated);
      }
    }
  }, [projects]);

  const handleExpand = () => {
    setExpanded((prev) => !prev);
  };

  const handleChange = (_, newAlignment) => {
    if (!newAlignment) return;

    if (newAlignment === 'clearall') {
      gridApi?.setFilterModel(null);
    } else {
      setAlignment(newAlignment);

      // Wait until initial state restored before changing columns
      if (hasRestoredColumnState) {
        setTimeout(() => {
          setColumnVisibility(gridApi, newAlignment);
        }, 0);
      }
    }
  };

  const handleCellValueChanged = (params) => {
    const { data, colDef } = params;
    const { field } = colDef;

    if (!data.projectId || !field) return;

    const updatedRow = {
      projectId: data.projectId,
      column: field,
      value: data[field],
    };

    setChangedRows((prev) => {
      const existingIndex = prev.findIndex(
        (row) => row.projectId === updatedRow.projectId && row.column === updatedRow.column
      );
      if (existingIndex !== -1) {
        const updatedRows = [...prev];
        updatedRows[existingIndex] = updatedRow;
        return updatedRows;
      }
      return [...prev, updatedRow];
    });
  };

  const handleViewCostClick = async (params) => {
    setCostLoading(true);
    setSelectedView('cost');
    try {
      const { projectId, afeNumber } = params.data;
      let rowData = { ...params.data, activities: [], purchaseOrders: [] };

      const [activities, purchaseOrders] = await Promise.all([
        fetchActivityData(projectId),
        fetchProjectPurchaseOrders(afeNumber),
      ]);

      rowData = { ...rowData, activities, purchaseOrders };
      setSelectedRow(rowData);
    } catch (err) {
      console.error('Error fetching data for cost:', err);
    } finally {
      setCostLoading(false);
    }
  };

  const handleCloseView = () => {
    setSelectedRow(null);
    setSelectedView(null);
  };

  const handleSaveAll = async () => {
    if (!Array.isArray(changedRows) || changedRows.length === 0) return;

    try {
      const groupedUpdates = changedRows.reduce((acc, row) => {
        if (!acc[row.projectId]) acc[row.projectId] = [];
        acc[row.projectId].push({
          op: 'replace',
          path: `/${row.column}`,
          value: row.value,
        });
        return acc;
      }, {});

      for (const [projectId, payload] of Object.entries(groupedUpdates)) {
        const result = await SaveProjectTableEdits(projectId, payload);
        if (!result.success) {
          console.error(`Failed to update Project ID: ${projectId}`);
        }
      }

      setChangedRows([]);
      toast.success('All changes saved successfully.');
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error('Failed to save changes. Please try again.');
    }
  };

  const defaultColDef = {
    minWidth: 200,
    sortable: true,
    filter: true,
    resizable: true,
    cellStyle: { textAlign: 'left' },
  };

  if (selectedView === 'notes' && selectedRow) {
    return <ProjectNotes rowData={selectedRow} onClose={handleCloseView} />;
  }

  if (selectedView === 'cost' && selectedRow && !costLoading) {
    return <AdaptiveCost rowData={selectedRow} onClose={() => setSelectedRow(null)} refetch={refetch} />;
  }

  function saveFilterState(params) {
    try {
      const filterState = params.api.getFilterModel();
      localStorage.setItem('filterPSState', JSON.stringify(filterState));
    } catch (error) {
      console.error('Error saving filter state:', error);
    }
  }

  function saveColumnVisibleState(params) {
    // Only save after state has been restored, not during grid init
    if (!hasRestoredColumnState) return;

    try {
      const columnState = params.api.getColumnState();
      localStorage.setItem('columnPSVisibleState', JSON.stringify(columnState));
    } catch (error) {
      console.error('Error saving column visible state:', error);
    }
  }

  function applyTableState(api) {
    if (!api) return;
    const savedFilterState = localStorage.getItem('filterPSState');
    const savedColumnVisState = localStorage.getItem('columnPSVisibleState');

    if (savedFilterState) {
      api.setFilterModel(JSON.parse(savedFilterState));
    }
    if (savedColumnVisState) {
      api.applyColumnState({
        state: JSON.parse(savedColumnVisState),
        applyOrder: true,
      });
    }
  }

  // const rowSelection = useMemo(() => {
  //   return {
  //         mode: 'singleRow',
  //         checkboxes: false,
  //         enableClickSelection: true,
  //     };
  // }, []);

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Accordion expanded={expanded} onChange={handleExpand}>
        <AccordionSummary expandIcon={<ArrowDownwardIcon />} id="panel1-header">
          <Typography sx={{ flexGrow: 1 }} fontWeight="bold">
            Project Submission Metrics
          </Typography>
          <Typography variant="body1" fontWeight="bold" color="textSecondary">
            {expanded ? 'Collapse' : 'Expand'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <HeatMapCommits />
        </AccordionDetails>
      </Accordion>

      {/* Controls */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          padding: '10px',
          background: '#f9f9f9',
          borderRadius: '8px',
          boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.1)',
          width: '100%',
        }}
      >
        {isMobile ? (
          <FormControl variant="outlined" size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Filters</InputLabel>
            <Select value={alignment} onChange={(e) => handleChange(null, e.target.value)} label="Filters">
              <MenuItem value="all">All Columns</MenuItem>
              <MenuItem value="netsuite">NetSuite</MenuItem>
              <MenuItem value="projectmanager">Project Manager</MenuItem>
              <MenuItem value="gis">GIS</MenuItem>
              <MenuItem value="quorum">Quorum</MenuItem>
              <MenuItem value="workdayadaptive">Workday Adaptive</MenuItem>
            </Select>
          </FormControl>
        ) : (
          <ButtonGroup sx={{ flexWrap: 'nowrap', overflowX: 'auto', maxWidth: '100%' }}>
            <Button onClick={() => handleChange(null, 'all')} sx={{ backgroundColor: 'azure', color: 'black' }}>
              All Columns
            </Button>
            <Button
              onClick={() => handleChange(null, 'netsuite')}
              sx={{ backgroundColor: 'mediumturquoise', color: 'black' }}
            >
              NetSuite
            </Button>
            <Button
              onClick={() => handleChange(null, 'projectmanager')}
              sx={{ backgroundColor: 'darkseagreen', color: 'black' }}
            >
              Project Manager
            </Button>
            <Button onClick={() => handleChange(null, 'gis')} sx={{ backgroundColor: 'lightblue', color: 'black' }}>
              GIS
            </Button>
            <Button onClick={() => handleChange(null, 'quorum')} sx={{ backgroundColor: 'steelblue', color: 'black' }}>
              Quorum
            </Button>
            <Button
              onClick={() => handleChange(null, 'workdayadaptive')}
              sx={{ backgroundColor: 'sandybrown', color: 'black' }}
            >
              Workday Adaptive
            </Button>
          </ButtonGroup>
        )}

        <Button variant="contained" color="error" onClick={() => handleChange(null, 'clearall')}>
          <ClearIcon sx={{ mr: 1 }} /> Clear Filters
        </Button>

        <Button variant="contained" color="success" onClick={handleSaveAll}>
          <SaveIcon sx={{ mr: 1 }} /> Save All
        </Button>

        {changedRows.length > 0 && (
          <div
            style={{ background: 'tomato', color: 'black', padding: '8px', borderRadius: '5px', fontWeight: 'bold' }}
          >
            You have {changedRows.length} unsaved changes
          </div>
        )}
      </div>

      {/* Grid */}
      <div style={{ flex: 1, padding: '10px' }}>
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <div>Error: {error}</div>
        ) : (
          <AgGridReact
            rowData={projects}
            columnDefs={fullColumnDefs}
            defaultColDef={defaultColDef}
            tooltipShowDelay={500}
            enableCharts={true}
            cellSelection={true}
            animateRows
            components={{ progressBarCellRenderer: ProgressBarCellRenderer }}
            sideBar={{
              toolPanels: [
                {
                  id: 'columns',
                  labelDefault: 'Columns',
                  labelKey: 'columns',
                  iconKey: 'columns',
                  toolPanel: 'agColumnsToolPanel',
                },
                {
                  id: 'filters',
                  labelDefault: 'Filters',
                  labelKey: 'filters',
                  iconKey: 'filter',
                  toolPanel: 'agFiltersToolPanel',
                },
              ],
              defaultToolPanel: undefined, // could set to 'filters' or 'columns'
            }}
            onGridReady={(params) => {
              setGridApi(params.api);

              // Delay to allow columnDefs (esp. nested/groups) to stabilize
              setTimeout(() => {
                applyTableState(params.api);
                setHasRestoredColumnState(true);
              }, 0);
            }}
            onFilterChanged={saveFilterState}
            onColumnVisible={saveColumnVisibleState}
            onCellValueChanged={handleCellValueChanged}
            onSortChanged={saveColumnVisibleState}
          />
        )}
      </div>
    </div>
  );
};

export default ProjectTracker;
